/**
 * Consulta periódica ao /v1/u1/health.
 *
 * O intervalo é longo de propósito: isto é um painel de estado, não um
 * monitor. Bater de segundo em segundo só produziria ruído e tráfego. Quando a
 * aba está oculta a consulta é suspensa — voltar a ela dispara uma imediata,
 * que é o momento em que o dado realmente importa.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchHealth, type HealthReading } from './health';

const POLL_INTERVAL_MS = 30_000;

export function useHealth(apiBase: string): HealthReading & { refresh: () => void } {
  const [reading, setReading] = useState<HealthReading>({
    phase: 'loading',
    health: null,
    error: null,
    checkedAt: null,
  });

  // contador de pedidos: o efeito reinicia quando ele muda, o que serve tanto
  // para o refresh manual quanto para o retorno da aba ao primeiro plano
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((t) => t + 1), []);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;

    const run = async (): Promise<void> => {
      try {
        const { health, error } = await fetchHealth(apiBase, controller.signal);
        if (!mounted.current) return;
        setReading({
          phase: health ? 'ok' : 'unreachable',
          health,
          error,
          checkedAt: Date.now(),
        });
      } catch {
        return; // abortado: o efeito já foi substituído, nada a registrar
      }
      if (!controller.signal.aborted) {
        timer = setTimeout(() => { void run(); }, POLL_INTERVAL_MS);
      }
    };

    void run();

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      controller.abort();
      if (timer !== undefined) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [apiBase, tick, refresh]);

  return { ...reading, refresh };
}
